import Immutable from 'immutable';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';

import { BackToLinkManager } from 'app/components/BackToLinkManager';
import ErrorPage from 'app/components/ErrorPage';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { selectCategoryPage } from 'app/selectors/BrowseSelector';
import useFeature from 'app/hooks/useFeature';
import BrowseCategoryPage from './BrowseCategoryPage';
import BrowseCategoryLeafPageContainer from './BrowseCategoryLeafPageContainer';
import BrowseCategoryNodePageContainer from './BrowseCategoryNodePageContainer';

const BrowseCategoryPageContainer = () => {
  const intl = useIntl();

  const pageState = useSelector(selectCategoryPage);

  const isCoreV2BrowsePageEnabled = useFeature(TOGGLZ_FEATURES.CORE_V2_BROWSE_PAGE);
  if (!isCoreV2BrowsePageEnabled) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const bibsByCategoryPath = pageState.get('bibsByCategoryPath')?.toJS() ?? {};
  const categoryPath = pageState.get('categoryPath');
  const categoryByPath = pageState.get('categoryByPath', Immutable.Map());
  const parentCategoryPath = pageState.get('parentCategoryPath');

  const category = categoryByPath.get(categoryPath)?.toJS();
  if (!category) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const isCategoryLeaf = category.subCategoryPaths.length === 0;
  const parentCategory = categoryByPath.get(parentCategoryPath)?.toJS();
  const backToPageTitle = parentCategory?.title ?? intl.formatMessage({ id: 'browse' });
  const backToPageScreenReaderLabel = intl.formatMessage(
    {
      defaultMessage: 'Back to {pageTitle}',
      id: 'sr_back_to_page_title'
    },
    {
      pageTitle: backToPageTitle
    }
  );

  const backToLinkProps = {
    href: `/v2/browse${parentCategoryPath || ''}`,
    label: backToPageTitle,
    screenReaderLabel: backToPageScreenReaderLabel
  };

  return (
    <>
      <BackToLinkManager
        label={backToPageTitle}
        pageId="BrowseCategoryPage"
        screenReaderLabel={backToPageScreenReaderLabel}
      />
      <BrowseCategoryPage backToLinkProps={backToLinkProps} category={category}>
        {isCategoryLeaf ? (
          <BrowseCategoryLeafPageContainer />
        ) : (
          <BrowseCategoryNodePageContainer
            bibsByCategoryPath={bibsByCategoryPath}
            category={category}
            categoryByPath={categoryByPath}
          />
        )}
      </BrowseCategoryPage>
    </>
  );
};

export default BrowseCategoryPageContainer;
